import {Icon, IFontStyles, ITextField, Label, Text, TextField, TooltipHost} from '@fluentui/react';
import React, {useEffect, useRef} from 'react';
import {Css} from "nano-css/addon/vcssom/cssToTree";

interface IEditableTextProps {
    value?: string;
    onChange: (value?: string) => void;
    label?: string;
    editableSuffix?: string;
    editablePrefix?: string;
    suffix?: string;
    prefix?: string;
    disabled?: boolean;
    placeholder?: string;
    valueRequired?: boolean;
    fontSize?: keyof IFontStyles;
    toolTip?: string;
    onRenderText?: (value: string) => React.ReactNode;
    style?: Css;
}

const EditableText: React.FC<IEditableTextProps> = (props) => {
    const [tempValue, setTempValue] = React.useState<string | undefined>(props.value?.toString());
    const [editMode, setEditMode] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);
    const textFieldRef = useRef<HTMLDivElement>(null);
    const textFieldInputRef = useRef<ITextField | null>(null);
    const [canDismiss, setCanDismiss] = React.useState<boolean>(true);
    
    useEffect(() => {
        setTempValue(props.value?.toString());
    }, [props?.value]);

    useEffect(() => {
        if (editMode && textFieldInputRef.current) {
            textFieldInputRef.current.focus();
        }
    }, [editMode]);
    
    const onDismiss = () => {
        if(canDismiss){
            props.onChange(tempValue?.trim());
            setEditMode(false);
        }
    }
    const handleClickOutside = React.useCallback((event: MouseEvent) => {
        if (textFieldRef.current && !textFieldRef.current.contains(event.target as Node)) {
            onDismiss();
        }
    }, [onDismiss]);
    
    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside]);
    
    const changeValue = (value: string | undefined) => {
        setTempValue(() => value);
    }
    
    const onChange = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setErrorMessage(undefined)
        setCanDismiss(false);
        changeValue((newValue ?? ''));
        if((newValue === undefined || newValue.trim() === '' || newValue === ' ') && props.valueRequired){
            setErrorMessage('This field is required');
        }
        else{
            setErrorMessage(undefined);
            setCanDismiss(true);
        }

    }
    
    const getTextValue = () => {
        return ((props?.prefix ?? '') + (tempValue ?? '') + (props.suffix ?? '')) ?? '';
    }

    const label = () => {
        // No label and nothing to explain: render nothing. An empty <Label> is not empty — it keeps
        // Fluent's padding and line box, which put a band of dead space above the text and left every
        // caller's value sitting lower than whatever it was meant to line up with.
        if (!props.label && !props.toolTip) return null;
        if (props.toolTip) {
            return (<TooltipHost content={props.toolTip}>
                <div>
                    <Label>
                        <Icon iconName={'Info'} style={{paddingRight: '5px'}}/>{props.label}
                    </Label>
                </div>
            </TooltipHost>)
        }
        return (<Label>{props.label}</Label>)
    }
    
    const getTextElement = () => {
        const value = getTextValue();
        if(value === undefined || value === null || value === '') {
            return (
            <Text variant={props.fontSize ?? 'xLargePlus'} style={{color: '#dddddd'}}>
                {props.placeholder ?? 'Click to set value'}
            </Text>);
        }
        if(props.onRenderText != null){
            return props.onRenderText(value);
        }
        return <Text variant={props.fontSize ?? 'xLargePlus'} style={{...props.style}}>{value}</Text>;
    }


    return (
    <div>
        {label()}
        {(editMode && !props.disabled) ? 
        <div ref={textFieldRef}>
            <TextField
                componentRef={(input) => (textFieldInputRef.current = input || null)}
                disabled={props.disabled}
                value={tempValue ?? ''}
                onChange={onChange}
                onBlur={onDismiss}
                suffix={props.editableSuffix}
                prefix={props.editablePrefix}
                placeholder={props.placeholder}
                onKeyDown={(e) => {
                    if(e.key === "Enter"){
                        onDismiss();
                    }
                    if(e.key == "Escape") {
                        setTempValue(props.value?.toString());
                        setEditMode(false);
                    }
                }}
                errorMessage={errorMessage}
            />
        </div> : 
            <div className = "hover-underline" onClick={() => setEditMode(true)} onSelect={() => setEditMode(true)}>
                {getTextElement()}
            </div>
        }
    </div>)
}

export default EditableText;